import React, { useContext, useState, useEffect } from "react";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import { AppContext } from "../../context/app.context";
import { ModelCxContext } from "../../context/modelCx.context";
import {
  FILTERS_SURFACE_ARRAY,
  FILTERS_CLASSIFICATION_ARRAY,
  FILTERS_CREW_ARRAY,
  FILTERS_CREW_SURFACE_ARRAY,
} from "../../utility/keys";
import Checkbox from "../../components/Checkbox";

// - - - - - - - - - -

const PREDEFINED_COMPANIES_LIST =
  process.env.REACT_APP_ENV !== "production"
    ? [
        {
          isChecked: true,
          title: "KI",
        },
        {
          isChecked: false,
          title: "SCG",
        },
        {
          isChecked: false,
          title: "KM",
        },
      ]
    : [
        {
          isChecked: true,
          title: "KI",
        },
        {
          isChecked: false,
          title: "SCG",
        },
      ];

// - - - - - - - - - -

const PREDEFINED_TAB_LIST = [
  {
    isChecked: true,
    title: "optimizer",
  },
  {
    isChecked: true,
    title: "scheduler",
  },
  {
    isChecked: true,
    title: "jobList",
  },
  {
    isChecked: false,
    title: "logs",
  },
  {
    isChecked: false,
    title: "userList",
  },
  // #202 — feature access (Compass / Dashboard / Time Machine) now lives in the
  // same screenAccessList array as the nav tabs, managed here in one place. These
  // are FEATURE toggles, not landing tabs (see FEATURE_ACCESS_TABS) — gated by
  // canAccessCompass / canAccessDashboard / canAccessTimeMachine.
  {
    isChecked: false,
    title: "compass",
  },
  {
    isChecked: false,
    title: "dashboard",
  },
  {
    isChecked: false,
    title: "timeMachine",
  },
  {
    isChecked: false,
    title: "ticketMaster",
  },
];

// #202 — screenAccessList tokens that grant a header FEATURE (button/modal), not
// a navigable main tab. Shown in Tab Access, but excluded from the "Default Tab"
// dropdown (you can't land on Compass/Dashboard/Time Machine).
const FEATURE_ACCESS_TABS = ["compass", "dashboard", "timeMachine", "ticketMaster"];

// Friendly display labels for screenAccessList tokens (stored values unchanged).
const TAB_LABELS = {
  optimizer: "Optimizer",
  scheduler: "Scheduler",
  jobList: "Jobs",
  logs: "Logs",
  userList: "Users",
  compass: "Compass",
  dashboard: "Dashboard",
  timeMachine: "Time Machine",
  ticketMaster: "Ticket Master",
};
const tabLabel = (title) => TAB_LABELS[title] || title;

// - - - - - - - - - -

function UserDetailModal({ currentUser, setOpenUserDetailModal }) {
  const { locationArray } = useContext(AppContext);
  const { setUserById } = useContext(ModelCxContext);
  const [changesFlag, setChangesFlag] = useState(false);
  const [companiesList, setCompaniesList] = useState(PREDEFINED_COMPANIES_LIST);
  const [defaultCompany, setDefaultCompany] = useState(
    currentUser.defaultCompany
  );
  const [defaultLocation, setDefaultLocation] = useState(
    currentUser.defaultLocation
  );
  const [defaultUImode, setDefaultUImode] = useState(currentUser.defaultUImode);
  const [tabsList, setTabsList] = useState(PREDEFINED_TAB_LIST);
  const [defaultTab, setDefaultTab] = useState(currentUser.defaultTab);
  const [surfaceFiltersList, setSurfaceFiltersList] = useState(
    FILTERS_SURFACE_ARRAY
  );
  const [classificationFiltersList, setClassificationFiltersList] = useState(
    FILTERS_CLASSIFICATION_ARRAY
  );
  const [crewFiltersList, setCrewFiltersList] = useState(FILTERS_CREW_ARRAY);
  const [crewSurfaceFiltersList, setCrewSurfaceFiltersList] = useState(
    FILTERS_CREW_SURFACE_ARRAY
  );
  const [conversion, setConversion] = useState(currentUser.conversion || "both");
  // Convex experiment (#191): mongoOnly (default) | convexOnly | both.
  // Mirrors `conversion`; gates the Mongo-vs-Convex backend per user.
  const [backendMode, setBackendMode] = useState(
    currentUser.backendMode || "mongoOnly"
  );
  const [devTools, setDevTools] = useState(currentUser.devTools ?? true);

  // - - - - - - - - - -

  const onSave = () => {
    const currentMongoId = currentUser._id?.toString();

    // convert local arrays to stored arrays
    const companyList = companiesList
      .filter((company) => company.isChecked)
      .map((company) => company.title);
    const screenAccessList = tabsList
      .filter((company) => company.isChecked)
      .map((company) => company.title);
    const surfaceFilters = surfaceFiltersList.map(
      (company) => company.isChecked
    );
    const classificationFilters = classificationFiltersList.map(
      (company) => company.isChecked
    );
    const crewFilters = crewFiltersList.map((company) => company.isChecked);
    const crewSurfaceFilters = crewSurfaceFiltersList.map(
      (company) => company.isChecked
    );

    //only save if changes have been made
    if (changesFlag) {
      // save locally
      currentUser.companyList = companyList;
      currentUser.defaultCompany = defaultCompany;
      currentUser.defaultLocation = defaultLocation;
      currentUser.defaultUImode = defaultUImode;
      currentUser.screenAccessList = screenAccessList;
      currentUser.defaultTab = defaultTab;
      currentUser.defaultFilters.surfaceFilters = surfaceFilters;
      currentUser.defaultFilters.classificationFilters = classificationFilters;
      currentUser.defaultFilters.crewFilters = crewFilters;
      currentUser.defaultFilters.crewSurfaceFilters = crewSurfaceFilters;
      currentUser.conversion = conversion;
      currentUser.backendMode = backendMode;
      currentUser.devTools = devTools;

      // save to MongoDB via TanStack mutation
      setUserById.mutate({
        filter: { _id: currentMongoId },
        update: {
          $set: {
            companyList: companyList,
            defaultCompany: defaultCompany,
            defaultLocation: defaultLocation,
            defaultUImode: defaultUImode,
            screenAccessList: screenAccessList,
            defaultTab: defaultTab,
            "defaultFilters.surfaceFilters": surfaceFilters,
            "defaultFilters.classificationFilters": classificationFilters,
            "defaultFilters.crewFilters": crewFilters,
            "defaultFilters.crewSurfaceFilters": crewSurfaceFilters,
            conversion: conversion,
            backendMode: backendMode,
            devTools: devTools,
          },
        },
      });

      // Close modal window
      setOpenUserDetailModal({ show: false });
    }

    console.log(
      "UserDetailModal onSave - companiesList[changed]: ",
      companiesList,
      ", defaultCompany: ",
      defaultCompany,
      ", defaultLocation: ",
      defaultLocation,
      ", defaultUImode: ",
      defaultUImode,
      ", tabsList: ",
      tabsList,
      ", defaultTab: ",
      defaultTab,
      ", surfaceFiltersList: ",
      surfaceFiltersList,
      ", classificationFiltersList: ",
      classificationFiltersList,
      ", crewFiltersList: ",
      crewFiltersList,
      ", crewSurfaceFiltersList: ",
      crewSurfaceFiltersList
    );
  };

  // - - - - - - - - - -

  const onCancel = () => {
    if (
      window.confirm("Are you sure you want to exit without saving changes?")
    ) {
      // User clicked OK
      // Close modal window
      setOpenUserDetailModal({ show: false });
    } else {
      // User clicked Cancel
    }
  };

  // - - - - - - - - - -

  const onCompaniesChanged = (event, companyIndex) => {
    setChangesFlag(true);
    const value = event.target.checked;
    setCompaniesList(() =>
      companiesList.map((company, index) => {
        if (companyIndex === index) {
          return {
            isChecked: value,
            title: company.title,
          };
        }
        return company;
      })
    );
  };

  // - - - - - - - - - -

  const onDefaultCompanyChanged = (value) => {
    setChangesFlag(true);
    setDefaultCompany(value);

    console.log("UserDetailModal onDefaultCompanyChanged value: ", value);
  };

  // - - - - - - - - - -

  const onDefaultLocationChanged = (value) => {
    setChangesFlag(true);
    setDefaultLocation(value);

    console.log("UserDetailModal onDefaultLocationChanged value: ", value);
  };

  // - - - - - - - - - -

  const onDefaultUImodeChanged = (value) => {
    setChangesFlag(true);
    setDefaultUImode(value);

    console.log("UserDetailModal onDefaultUImodeChanged value: ", value);
  };

  // - - - - - - - - - -

  const onTabsAccessChanged = (event, tabsIndex) => {
    setChangesFlag(true);
    const value = event.target.checked;
    setTabsList(() =>
      tabsList.map((tab, index) => {
        if (tabsIndex === index) {
          return {
            isChecked: value,
            title: tab.title,
          };
        }
        return tab;
      })
    );
  };

  // - - - - - - - - - -

  const onDefaultTabChanged = (value) => {
    setChangesFlag(true);
    setDefaultTab(value);

    console.log("UserDetailModal onDefaultTabChanged value: ", value);
  };

  // - - - - - - - - - -

  const onSurfaceFiltersChanged = (event, surfaceIndex) => {
    setChangesFlag(true);
    const value = event.target.checked;
    setSurfaceFiltersList(() =>
      surfaceFiltersList.map((surface, index) => {
        if (surfaceIndex === index) {
          return {
            isChecked: value,
            name: surface.name,
          };
        }
        return surface;
      })
    );
  };

  // - - - - - - - - - -

  const onClassificationFiltersChanged = (event, classificationIndex) => {
    setChangesFlag(true);
    const value = event.target.checked;
    setClassificationFiltersList(() =>
      classificationFiltersList.map((classification, index) => {
        if (classificationIndex === index) {
          return {
            isChecked: value,
            name: classification.name,
          };
        }
        return classification;
      })
    );
  };

  // - - - - - - - - - -

  const onCrewFiltersChanged = (event, crewIndex) => {
    setChangesFlag(true);
    const value = event.target.checked;
    setCrewFiltersList(() =>
      crewFiltersList.map((crew, index) => {
        if (crewIndex === index) {
          return {
            isChecked: value,
            name: crew.name,
          };
        }
        return crew;
      })
    );
  };

  // - - - - - - - - - -

  const onCrewSurfaceFiltersChanged = (event, crewSurfaceIndex) => {
    setChangesFlag(true);
    const value = event.target.checked;
    setCrewSurfaceFiltersList(() =>
      crewSurfaceFiltersList.map((crewSurface, index) => {
        if (crewSurfaceIndex === index) {
          return {
            isChecked: value,
            name: crewSurface.name,
          };
        }
        return crewSurface;
      })
    );
  };

  // - - - - - - - - - -

  const onConversionChanged = (value) => {
    setChangesFlag(true);
    setConversion(value);
  };

  // - - - - - - - - - -

  const onBackendModeChanged = (value) => {
    setChangesFlag(true);
    setBackendMode(value);
  };

  // - - - - - - - - - -

  const onDevToolsChanged = (event) => {
    setChangesFlag(true);
    setDevTools(event.target.checked);
  };

  // - - - - - - - - - -

  useEffect(() => {
    const companiesArr = currentUser?.companyList;
    const tabsArr = currentUser?.screenAccessList;
    const surfaceFiltersArr = currentUser?.defaultFilters?.surfaceFilters;
    const classificationFiltersArr =
      currentUser?.defaultFilters?.classificationFilters;
    const crewFiltersArr = currentUser?.defaultFilters?.crewFilters;
    const crewSurfaceFiltersArr =
      currentUser?.defaultFilters?.crewSurfaceFilters;

    console.log(
      "UserDetailModal useEffect[] companiesArr: ",
      companiesArr,
      ", companiesList[init]: ",
      companiesList,
      ", tabsList[init]: ",
      tabsList,
      ", surfaceFiltersArr[init]: ",
      surfaceFiltersArr,
      ", classificationFiltersArr[init]: ",
      classificationFiltersArr,
      ", crewFiltersArr[init]: ",
      crewFiltersArr,
      ", crewSurfaceFiltersArr[init]: ",
      crewSurfaceFiltersArr
    );

    setCompaniesList(() =>
      companiesList.map((company) => ({
        ...company,
        isChecked: companiesArr.indexOf(company.title) > -1,
      }))
    );

    setTabsList(() =>
      tabsList.map((tab) => ({
        ...tab,
        isChecked: tabsArr.indexOf(tab.title) > -1,
      }))
    );

    setSurfaceFiltersList(() =>
      surfaceFiltersList.map((surface, index) => ({
        ...surface,
        isChecked: surfaceFiltersArr[index] ? surfaceFiltersArr[index] : false,
      }))
    );
    // ternary needed while a user might not yet have the new 11-element array (still using 7-element)

    setClassificationFiltersList(() =>
      classificationFiltersList.map((classification, index) => ({
        ...classification,
        isChecked: classificationFiltersArr[index],
      }))
    );

    setCrewFiltersList(() =>
      crewFiltersList.map((crew, index) => ({
        ...crew,
        isChecked: crewFiltersArr[index],
      }))
    );

    setCrewSurfaceFiltersList(() =>
      crewSurfaceFiltersList.map((crewSurface, index) => ({
        ...crewSurface,
        isChecked: crewSurfaceFiltersArr[index],
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // - - - - - - - - - -

  return (
    <Modal
      onClose={() =>
        !changesFlag ? setOpenUserDetailModal({ show: false }) : onCancel()
      }
    >
      <Modal.Header>
        <div className="flex justify-between">
          <div>User Information</div>
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className="flex flex-col gap-4 w-[400px]">
          <div className="text-lg font-bold">{currentUser.username}</div>
          <div className="flex flex-wrap gap-y-2">
            <div className="flex-1 basis-2/4">Company List</div>
            <div className="flex-1 basis-2/4">
              {companiesList.map((company, index) => (
                <Checkbox
                  key={index}
                  value={company.isChecked}
                  label={company.title}
                  checked={company.isChecked}
                  onChange={(event) => onCompaniesChanged(event, index)}
                />
              ))}
            </div>
            <div className="flex-1 basis-2/4">Default Company</div>
            <div className="flex-1 basis-2/4">
              <select
                className="bg-yellow-30 text-black text-sm font-medium hover:opacity-75 cursor-pointer"
                value={defaultCompany}
                onChange={(e) => onDefaultCompanyChanged(e.target.value)}
              >
                {companiesList.map((company, index) => (
                  <option key={index} value={company.title}>
                    {company.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1 basis-2/4">Default Location</div>
            <div className="flex-1 basis-2/4">
              <select
                className="bg-yellow-30 text-black text-sm font-medium hover:opacity-75 cursor-pointer"
                value={defaultLocation}
                onChange={(e) => onDefaultLocationChanged(e.target.value)}
              >
                {locationArray.map((location, index) => (
                  <option key={index} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1 basis-2/4">Default UI Mode</div>
            <div className="flex-1 basis-2/4">
              <select
                className="bg-yellow-30 text-black text-sm font-medium hover:opacity-75 cursor-pointer"
                value={defaultUImode}
                onChange={(e) => onDefaultUImodeChanged(e.target.value)}
              >
                <option value="dark">dark</option>
                <option value="light">light</option>
              </select>
            </div>
            <div className="flex-1 basis-2/4">Tab Access</div>
            <div className="flex-1 basis-2/4">
              {tabsList.map((tab, index) => (
                <Checkbox
                  key={index}
                  value={tab.isChecked}
                  label={tabLabel(tab.title)}
                  checked={tab.isChecked}
                  onChange={(event) => onTabsAccessChanged(event, index)}
                />
              ))}
            </div>
            <div className="flex-1 basis-2/4">Default Tab</div>
            <div className="flex-1 basis-2/4">
              <select
                className="bg-yellow-30 text-black text-sm font-medium hover:opacity-75 cursor-pointer"
                value={defaultTab}
                onChange={(e) => onDefaultTabChanged(e.target.value)}
              >
                {tabsList
                  .filter((tab) => !FEATURE_ACCESS_TABS.includes(tab.title))
                  .map((tab, index) => (
                    <option key={index} value={tab.title}>
                      {tabLabel(tab.title)}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <div className="text-lg font-bold">Developer Settings</div>
          <div className="flex flex-wrap gap-y-2">
            <div className="flex-1 basis-2/4">Data Source</div>
            <div className="flex-1 basis-2/4">
              <select
                className="bg-yellow-30 text-black text-sm font-medium hover:opacity-75 cursor-pointer"
                value={conversion}
                onChange={(e) => onConversionChanged(e.target.value)}
              >
                <option value="both">both</option>
                <option value="tanstackOnly">tanstackOnly</option>
                <option value="realmOnly">realmOnly</option>
              </select>
            </div>
            <div className="flex-1 basis-2/4">Backend (#191 Convex)</div>
            <div className="flex-1 basis-2/4">
              <select
                className="bg-yellow-30 text-black text-sm font-medium hover:opacity-75 cursor-pointer"
                value={backendMode}
                onChange={(e) => onBackendModeChanged(e.target.value)}
              >
                <option value="mongoOnly">mongoOnly (default)</option>
                <option value="convexOnly">convexOnly</option>
                {/* #191 — "both (compare)" removed: parity is confirmed
                    operator-side via the reconcile audit, not a per-user UI mode. */}
              </select>
            </div>
            <div className="flex-1 basis-2/4">DevTools</div>
            <div className="flex-1 basis-2/4">
              <Checkbox
                value={devTools}
                label="Show TanStack DevTools"
                checked={devTools}
                onChange={(event) => onDevToolsChanged(event)}
              />
            </div>
          </div>
          <div className="text-lg font-bold">Default Filters</div>
          <div className="flex flex-wrap gap-y-4">
            <div className="flex-1 basis-2/4">Surface</div>
            <div className="flex-1 basis-2/4">
              {surfaceFiltersList.map((surface, index) => (
                <Checkbox
                  key={index}
                  value={
                    // this ternary is required to eliminate an error/warning:
                    // "Warning: A component is changing an uncontrolled input to be controlled"
                    //   which is due to the initial State value from "keys.js"
                    //   doesn't have the isChecked element, only the name element
                    //   the useEffect[] adds it but evidently after the error is triggered
                    surface.isChecked ? surface.isChecked : false
                  }
                  label={surface.name}
                  checked={surface.isChecked ? surface.isChecked : false}
                  onChange={(event) => onSurfaceFiltersChanged(event, index)}
                />
              ))}
            </div>
            <div className="flex-1 basis-2/4">Classification</div>
            <div className="flex-1 basis-2/4">
              {classificationFiltersList.map((classification, index) => (
                <Checkbox
                  key={index}
                  value={
                    classification.isChecked ? classification.isChecked : false
                  }
                  label={classification.name}
                  checked={
                    classification.isChecked ? classification.isChecked : false
                  }
                  onChange={(event) =>
                    onClassificationFiltersChanged(event, index)
                  }
                />
              ))}
            </div>
            <div className="flex-1 basis-2/4">Crew</div>
            <div className="flex-1 basis-2/4">
              {crewFiltersList.map((crew, index) => (
                <Checkbox
                  key={index}
                  value={crew.isChecked ? crew.isChecked : false}
                  label={crew.name}
                  checked={crew.isChecked ? crew.isChecked : false}
                  onChange={(event) => onCrewFiltersChanged(event, index)}
                />
              ))}
            </div>
            <div className="flex-1 basis-2/4">Crew Surface</div>
            <div className="flex-1 basis-2/4">
              {crewSurfaceFiltersList.map((crewSurface, index) => (
                <Checkbox
                  key={index}
                  value={crewSurface.isChecked ? crewSurface.isChecked : false}
                  label={crewSurface.name}
                  checked={
                    crewSurface.isChecked ? crewSurface.isChecked : false
                  }
                  onChange={(event) =>
                    onCrewSurfaceFiltersChanged(event, index)
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div className="flex flex-col items-center w-full gap-4">
          <div className="h-8 flex justify-between w-full">
            <Button
              className="uppercase w-31"
              onClick={() => onSave()}
              disabled={!changesFlag}
            >
              Save
            </Button>
            {changesFlag ? (
              <Button
                color="light"
                className="uppercase w-31"
                onClick={() => onCancel()}
              >
                Cancel
              </Button>
            ) : (
              <></>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default UserDetailModal;
